import React, { useMemo, useCallback, useState } from 'react'
import WifiIcon from '@mui/icons-material/Wifi'
import LteMobiledataIcon from '@mui/icons-material/LteMobiledata'
import WindowIcon from '@mui/icons-material/Window'
import AndroidIcon from '@mui/icons-material/Android'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import landingLogo from '../img/landing-logo.png'
import moment from 'moment'
import useMobile from '../hooks/useMobile'

// Компонент тултипа (мобильно-дружественный)
const Tooltip = ({ children, title, onLongPress, isCheckingSpeed }) => {
    const [show, setShow] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [touchTimer, setTouchTimer] = useState(null)
    
    // Определяем, мобильное ли устройство
    React.useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window)
        }
        checkMobile()
        window.addEventListener('resize', checkMobile)
        return () => window.removeEventListener('resize', checkMobile)
    }, [])
    
    const handleTouchStart = (e) => {
        if (isMobile) {
            // Короткое касание - показываем тултип
            setShow(true)
            setTimeout(() => setShow(false), 3000)
            
            // Длительное зажатие - запускаем проверку скорости
            const timer = setTimeout(() => {
                if (onLongPress) {
                    console.log('Long press triggered!') // Отладка
                    onLongPress(e)
                }
            }, 600) // 600ms для длительного нажатия
            
            setTouchTimer(timer)
        }
    }
    
    const handleTouchEnd = (e) => {
        if (isMobile && touchTimer) {
            // Если отпустили до истечения 600ms, отменяем длительное нажатие
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    const handleTouchCancel = (e) => {
        if (isMobile && touchTimer) {
            clearTimeout(touchTimer)
            setTouchTimer(null)
        }
    }
    
    return (
        <div 
            style={{ 
                position: 'relative', 
                display: 'inline-block',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                WebkitTouchCallout: 'none',
                WebkitTapHighlightColor: 'transparent'
            }}
            onMouseEnter={() => !isMobile && setShow(true)}
            onMouseLeave={() => !isMobile && setShow(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
        >
            {/* Показываем спиннер только если идет проверка скорости */}
            {isCheckingSpeed ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    color: 'var(--primary-orange)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: '500'
                }}>
                    <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid var(--light-gray)',
                        borderTop: '2px solid var(--primary-orange)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }} />
                    <span>Testing...</span>
                </div>
            ) : (
                children
            )}
            {show && (
                <div style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--medium-gray)',
                    color: 'var(--text-light)',
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-xs)',
                    whiteSpace: 'nowrap',
                    zIndex: 1000,
                    border: '1px solid var(--light-gray)',
                    boxShadow: 'var(--shadow-md)',
                    marginBottom: '5px',
                    // На мобильных устройствах делаем тултип более заметным
                    ...(isMobile && {
                        backgroundColor: 'var(--dark-gray)',
                        border: '2px solid var(--primary-orange)',
                        fontSize: 'var(--text-sm)',
                        padding: '10px 12px'
                    })
                }}>
                    {title}
                </div>
            )}
        </div>
    )
}

// Компонент спиннера загрузки
const LoadingSpinner = () => (
  <div style={{
    width: '16px',
    height: '16px',
    border: '2px solid var(--light-gray)',
    borderTop: '2px solid var(--primary-orange)',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }} />
)

const MobileProxyTable = ({ 
  proxies, 
  selectedRow, 
  onRowClick, 
  onBuyClick, 
  onSpeedCheck, 
  isCheckingSpeed, 
  proxySpeed,
  speedCheckTimes,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  loadingButton
}) => {
  const { isMobile } = useMobile()
  // Мемоизируем функцию для вычисления возраста прокси
  const calculateProxyAge = useCallback((firstSeen) => {
    if (!firstSeen) return 'N/A'
    
    const now = new Date()
    const firstSeenDate = new Date(firstSeen)
    
    const years = now.getFullYear() - firstSeenDate.getFullYear()
    if (years >= 1) return `${years}y`
    
    const months = (now.getMonth() - firstSeenDate.getMonth()) + (years * 12)
    if (months >= 1) return `${months}m`
    
    const days = Math.floor((now - firstSeenDate) / (1000 * 60 * 60 * 24))
    if (days >= 1) return `${days}d`
    
    const hours = Math.floor((now - firstSeenDate) / (1000 * 60 * 60))
    return `${hours}h`
  }, [])

  // Мемоизируем список прокси для лучшей производительности
  const proxyList = useMemo(() => {
    return proxies?.agents || []
  }, [proxies?.agents])

  // Мемоизируем вычисления пагинации
  const paginationInfo = useMemo(() => {
    const total = proxies?.total || 0
    const totalPages = Math.ceil(total / rowsPerPage)
    const startItem = page * rowsPerPage + 1
    const endItem = Math.min((page + 1) * rowsPerPage, total)
    
    return {
      total,
      totalPages,
      startItem,
      endItem,
      isFirstPage: page === 0,
      isLastPage: page >= totalPages - 1
    }
  }, [proxies?.total, page, rowsPerPage])

  return (
    <div style={{ padding: 'var(--spacing-sm)' }}>
      {proxyList.map((row) => (
        <div
          key={row.id}
          onClick={() => onRowClick(row)}
          style={{
            background: 'var(--medium-gray)',
            border: '1px solid var(--light-gray)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-sm)',
            cursor: 'pointer',
            borderLeft: selectedRow?.id === row.id ? '4px solid var(--primary-orange)' : '4px solid transparent',
            transition: 'var(--transition-normal)'
          }}
        >
          {/* Заголовок карточки */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-lg)',
                fontWeight: '600',
                color: 'var(--text-light)',
                marginBottom: 'var(--spacing-xs)'
              }}>
                {row?.ip.replace(/\_/g, '*')}
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)'
              }}>
                {row?.loc?.city}, {row?.loc?.reg} • {row?.loc?.zip}
              </div>
            </div>
            <div style={{
              fontSize: 'var(--text-lg)',
              fontWeight: '700',
              color: 'var(--primary-orange)'
            }}>
              ${row?.priceShrC ? (row.priceShrC/100).toFixed(2) : '0.00'}
            </div>
          </div>

          {/* Детали прокси */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)'
          }}>
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                ISP & Device
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span>{row?.loc?.isp?.replace(/(.{10})..+/, "$1…")}</span>
                {row?.dev === 'windows' ? (
                  <WindowIcon style={{ fontSize: 16, color: 'var(--primary-orange)' }} />
                ) : row?.dev === 'android' ? (
                  <AndroidIcon style={{ fontSize: 16, color: 'var(--primary-orange)' }} />
                ) : null}
                {row?.conn === 'wifi' ? (
                  <WifiIcon style={{ fontSize: 16, color: 'var(--primary-orange)' }} />
                ) : (
                  <LteMobiledataIcon style={{ fontSize: 16, color: 'var(--primary-orange)' }} />
                )}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Users
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.leases?.count > 0 ? row?.leases?.count + '/4' : row?.leases?.count}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Age
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.probeActv !== undefined ? (
                  <span style={{ color: 'var(--primary-orange)' }}>
                    {calculateProxyAge(row?.firstSeen)}
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                )}
              </div>
            </div>
            
            <div>
              <div style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--text-muted)',
                marginBottom: '4px',
                fontWeight: '500'
              }}>
                Activity
              </div>
              <div style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-light)'
              }}>
                {row?.probeActv !== undefined ? (
                  <span style={{ color: 'var(--primary-orange)' }}>
                    {Math.round(row?.probeActv * 100)}%
                  </span>
                ) : (
                  <span style={{ color: 'var(--text-muted)' }}>N/A</span>
                )}
              </div>
            </div>
          </div>

          {/* Кнопки действий */}
          <div style={{
            display: 'flex',
            gap: 'var(--spacing-sm)',
            marginTop: 'var(--spacing-sm)'
          }}>
            {/* Результат скорости с тултипом */}
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '8px 12px',
              background: 'var(--dark-gray)',
              border: '1px solid var(--light-gray)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-light)',
              fontSize: 'var(--text-sm)',
              fontWeight: '500'
            }}>
              <Tooltip 
                title={speedCheckTimes?.[row?.id] ? moment(speedCheckTimes[row?.id]).fromNow() : 'Hold to test speed'}
                onLongPress={() => {
                  console.log('Long press triggered for proxy:', row?.id)
                  onSpeedCheck(row?.id)
                }}
                isCheckingSpeed={isCheckingSpeed[row?.id] === true}
              >
                <span>{proxySpeed[row?.id] != null ? `${proxySpeed[row?.id]} Mbps` : 'Check speed'}</span>
              </Tooltip>
            </div>
            
            <button 
              className="btn btn-buy btn-small"
              onClick={(e) => {
                e.stopPropagation()
                onBuyClick(row)
              }}
              style={{ flex: 1 }}
            >
              Buy
            </button>
          </div>
        </div>
      ))}
      
      {/* Мобильная пагинация */}
      {proxyList.length > 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-sm)',
          padding: 'var(--spacing-md)',
          background: 'var(--medium-gray)',
          border: '1px solid var(--light-gray)',
          borderRadius: 'var(--radius-lg)',
          marginTop: 'var(--spacing-md)'
        }}>
          {/* Строка с количеством строк на странице */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            gap: 'var(--spacing-sm)' 
          }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
              Rows per page:
            </span>
            <select 
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-light)',
                padding: '6px 8px',
                fontSize: 'var(--text-sm)',
                minWidth: '60px'
              }}
            >
              {[10, 20, 30, 40, 50].map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
          
          {/* Строка с навигацией */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'var(--spacing-sm)'
          }}>
            <button 
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                color: 'var(--text-light)',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                transition: 'var(--transition-normal)',
                fontSize: 'var(--text-sm)',
                minWidth: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => onPageChange(null, 0, 'first')}
              disabled={paginationInfo.isFirstPage || loadingButton}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = 'var(--primary-orange)'
                  e.target.style.borderColor = 'var(--primary-orange)'
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = 'var(--dark-gray)'
                  e.target.style.borderColor = 'var(--light-gray)'
                }
              }}
            >
              {loadingButton === 'first' ? <LoadingSpinner /> : <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />}
            </button>
            
            <button 
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                color: 'var(--text-light)',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                cursor: 'pointer',
                transition: 'var(--transition-normal)',
                fontSize: 'var(--text-sm)',
                minWidth: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => onPageChange(null, Math.max(0, page - 1), 'prev')}
              disabled={paginationInfo.isFirstPage || loadingButton}
              onMouseEnter={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = 'var(--primary-orange)'
                  e.target.style.borderColor = 'var(--primary-orange)'
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled) {
                  e.target.style.background = 'var(--dark-gray)'
                  e.target.style.borderColor = 'var(--light-gray)'
                }
              }}
            >
              {loadingButton === 'prev' ? <LoadingSpinner /> : <KeyboardArrowLeft style={{ fontSize: '14px' }} />}
            </button>
            
            <span style={{
              fontSize: 'var(--text-sm)',
              color: 'var(--text-muted)',
              margin: '0 var(--spacing-xs)',
              whiteSpace: 'nowrap',
              minWidth: '80px',
              textAlign: 'center'
            }}>
              {paginationInfo.startItem}-{paginationInfo.endItem} of {paginationInfo.total}
            </span>
            
            <button 
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                color: 'var(--text-light)',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                cursor: (paginationInfo.isLastPage || loadingButton) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-normal)',
                fontSize: 'var(--text-sm)',
                minWidth: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (paginationInfo.isLastPage || loadingButton) ? 0.5 : 1
              }}
              onClick={() => onPageChange(null, Math.min(paginationInfo.totalPages - 1, page + 1), 'next')}
              disabled={paginationInfo.isLastPage || loadingButton}
              onMouseEnter={(e) => {
                if (!e.target.disabled && !loadingButton) {
                  e.target.style.background = 'var(--primary-orange)'
                  e.target.style.borderColor = 'var(--primary-orange)'
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled && !loadingButton) {
                  e.target.style.background = 'var(--dark-gray)'
                  e.target.style.borderColor = 'var(--light-gray)'
                }
              }}
            >
              {loadingButton === 'next' ? <LoadingSpinner /> : <KeyboardArrowRight style={{ fontSize: '14px' }} />}
            </button>
            
            <button 
              style={{
                background: 'var(--dark-gray)',
                border: '1px solid var(--light-gray)',
                color: 'var(--text-light)',
                padding: '8px',
                borderRadius: 'var(--radius-sm)',
                cursor: (paginationInfo.isLastPage || loadingButton) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-normal)',
                fontSize: 'var(--text-sm)',
                minWidth: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: (paginationInfo.isLastPage || loadingButton) ? 0.5 : 1
              }}
              onClick={() => onPageChange(null, Math.max(0, paginationInfo.totalPages - 1), 'last')}
              disabled={paginationInfo.isLastPage || loadingButton}
              onMouseEnter={(e) => {
                if (!e.target.disabled && !loadingButton) {
                  e.target.style.background = 'var(--primary-orange)'
                  e.target.style.borderColor = 'var(--primary-orange)'
                }
              }}
              onMouseLeave={(e) => {
                if (!e.target.disabled && !loadingButton) {
                  e.target.style.background = 'var(--dark-gray)'
                  e.target.style.borderColor = 'var(--light-gray)'
                }
              }}
            >
              {loadingButton === 'last' ? <LoadingSpinner /> : <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default MobileProxyTable
